import { useCallback, useMemo } from 'react';
import { updateIcon } from '../services/authApi';
import { toRelativePath } from '../utils/videoImagePath';
import { useApi } from './useApi';
import { useImageProcessor } from './useImageProcessor';
import { User } from '../types/entities';

interface IconUpdateProps {
  currentUser: User;
  onUserUpdate: (user: User) => void;
}

export const useIconUpdate = ({ currentUser, onUserUpdate }: IconUpdateProps) => {
  // 画像処理フックを利用
  const imageProcessor = useImageProcessor({
    maxSizeMB: 1,
    maxWidthOrHeight: 1000,
  });
  
  // アイコンのフルURL（キャッシュバスターを追加して即時反映を確保）
  // icon_pathが変更されたときに確実に新しいURLが生成されるようにする
  // currentUserオブジェクト全体を依存配列に含めて、確実に再計算されるようにする
  const iconUrl = useMemo(() => {
    if (!currentUser.icon_path || currentUser.icon_path.trim() === '') return null;
    // icon_pathの値自体をキャッシュバスターとして使用
    // これにより、icon_pathが変更されたときに確実に新しいURLが生成される
    // パスに含まれる特殊文字をエンコードして使用
    const cacheBuster = encodeURIComponent(currentUser.icon_path);
    return `${toRelativePath(currentUser.icon_path)}?v=${cacheBuster}`;
  }, [currentUser.icon_path, currentUser]);
  
  // API呼び出しのフックを利用
  const updateIconApi = useApi(
    async (file: File) => {
      const formData = new FormData();
      formData.append('icon', file);
      const response = await updateIcon(formData);
      return response;
    },
    {
      successMessage: 'アイコンを更新しました',
      errorMessage: 'アイコン更新に失敗しました',
      onSuccess: (response) => {
        // iconPathが有効な場合のみ更新
        if (response.iconPath && response.iconPath.trim() !== '') {
          // ユーザー情報の icon_path を更新（新しいオブジェクト参照を作成して確実に再レンダリング）
          const updatedUser = {
            ...currentUser,
            icon_path: response.iconPath
          };
          onUserUpdate(updatedUser);
        } else {
          console.warn('Icon path is empty in response:', response);
        }
        
        // 状態をリセット
        imageProcessor.resetState();
        
        // アイコン更新を確実に反映するため、ページをリロード
        setTimeout(() => {
          window.location.reload();
        }, 500); // 成功メッセージを表示してからリロード
      }
    }
  );
  
  // アイコン更新の送信処理
  const handleIconSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!imageProcessor.file) {
      return;
    }
    
    await updateIconApi.execute(imageProcessor.file);
  }, [imageProcessor.file, updateIconApi]);
  
  // 編集済み画像の保存
  const handleSaveEdit = useCallback(async () => {
    const editedFile = await imageProcessor.saveEditedImage();
    return editedFile;
  }, [imageProcessor]);

  return {
    ...imageProcessor,
    iconUrl,
    isUpdating: updateIconApi.loading,
    error: updateIconApi.error,
    handleIconSubmit,
    handleSaveEdit,
  };
}; 